// src/components/UserAvatar.jsx - Updated with profile navigation

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const AvatarContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const AvatarButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: none;
  padding: 0;

  &:hover {
    border-color: #d1d5db;
    transform: scale(1.05);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 8px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  min-width: 200px;
  z-index: 50;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
`;

const DropdownHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 8px 8px 0 0;
`;

const UserEmail = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  word-break: break-all;
`;

const UserRole = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #f3f4f6;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
    color: #dc2626;
    
    &:hover {
      background-color: #fef2f2;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UserAvatar = ({ user, onLogout, getAvatarUrl }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const avatarRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        avatarRef.current &&
        !avatarRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAvatarClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    setIsDropdownOpen(false);
  };

  const handleProfile = () => {
    navigate('/profile');
    setIsDropdownOpen(false);
  };

  const avatarUrl = getAvatarUrl ? getAvatarUrl() : `https://robohash.org/${user?.email || 'default'}?set=set1&size=200x200`;

  return (
    <AvatarContainer>
      <AvatarButton 
        ref={avatarRef}
        onClick={handleAvatarClick}
        title={`${user?.email || 'User'} - Click for menu`}
      >
        <AvatarImage 
          src={avatarUrl} 
          alt={`${user?.email || 'User'} avatar`}
          onError={(e) => {
            // Fallback to a default RoboHash if the specific one fails
            e.target.src = 'https://robohash.org/default?set=set1&size=200x200';
          }}
        />
      </AvatarButton>

      <DropdownMenu ref={dropdownRef} isOpen={isDropdownOpen}>
        <DropdownHeader>
          <UserEmail>{user?.email || 'User'}</UserEmail>
          <UserRole>{user?.is_admin ? 'Admin' : 'Analyst'}</UserRole>
        </DropdownHeader>

        <DropdownItem onClick={handleProfile}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
          Profile Settings
        </DropdownItem>

        {user?.is_admin && (
          <DropdownItem onClick={() => { navigate('/admin'); setIsDropdownOpen(false); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
            </svg>
            Admin Panel
          </DropdownItem>
        )}

        <DropdownItem onClick={handleLogout}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16,17 21,12 16,7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
          Logout
        </DropdownItem>
      </DropdownMenu>
    </AvatarContainer>
  );
};

export default UserAvatar;